import b from 'b_';
import React, { useCallback, useEffect, useState } from 'react';

import QuizStudentAnswerStatus from '../__answer-status/quiz-student__answer-status';
import QuizStudentFinisher from '../__finisher/quiz-student__finisher';
import { QuizStudentQuestion } from '../__question/quiz-student__question';
import QuizStudentWrapper from '../__wrapper/quiz-student__wrapper';
import { useSocketRoom } from '../../../utils/hooks/useSocketRoom';
import { useAppContext } from '../../app-factory/context';
import { SPIN_SIZES, UI_KIT_HEADER_TYPOGRAPHY } from '../../constants';
import { H1, H2, H3, H4 } from '../../header-text/header-text';
import { AnswerStatus, CARD_COLORS, CurrentStudentAnswer } from '../../quiz/typings';
import { Question } from '../../quiz/typings';
import Spin from '../../spin/spin';
import { useQuizStudentId } from '../hooks/use-quiz-student-id';

interface Props {
    pin: string;
}

export const QuizStudentGame = ({ pin }: Props) => {
    const {
        uatraits: { BrowserName, OSFamily },
    } = useAppContext();

    const [name, setName] = useState('');
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [currentQuestionAnswer, setCurrentQuestionAnswer] = useState<CurrentStudentAnswer | null>(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number | null>(null);
    const [questionsCount, setQuestionsCount] = useState<number | null>(null);
    const [answerStatus, setAnswerStatus] = useState<AnswerStatus | null>(null);
    const [countCorrectAnswers, setCountCorrectAnswers] = useState(0);
    const [isCompleted, setCompleted] = useState(false);
    const [isVisibleCorrectAnswer, setIsVisibleCorrectAnswer] = useState(false);

    const id = useQuizStudentId();

    const showAnswerStatus = (status: AnswerStatus) => {
        if (status === 'correct') {
            setCountCorrectAnswers(countCorrectAnswers + 1);
        }
        setAnswerStatus(status);
    };

    const handleQuizRestart = () => {
        setCompleted(false);
        setCurrentQuestion(null);
        setCurrentQuestionIndex(null);
        setAnswerStatus(null);
        setCountCorrectAnswers(0);
    };

    const room = `quiz:${pin}`;
    const { emit } = useSocketRoom(room, {
        question: ({
            question,
            questionIndex,
            questionsCount: totalQuestions,
        }: {
            question: Question;
            questionIndex: number;
            questionsCount: number;
        }) => {
            setIsVisibleCorrectAnswer(false);
            setCurrentQuestionAnswer(null);
            setCurrentQuestion(question);
            setCurrentQuestionIndex(questionIndex);
            setQuestionsCount(totalQuestions);
            setAnswerStatus('pending');
            setCompleted(false);
        },
        'show.results': () => setCompleted(true),
        'quiz:reset': handleQuizRestart,
        'quiz:showCorrectAnswer': () => {
            setIsVisibleCorrectAnswer(true);
            setCurrentQuestion(null);
        },
    });

    const handlePageShow = useCallback(() => emit('student.join', { id }), [emit, id]);

    const handlePageHide = useCallback(() => emit('student.left', { id }), [emit, id]);

    const handleBeforeUnload = useCallback(() => emit('student.left', { id }), [emit, id]);

    const handleBlur = useCallback(() => {
        if (BrowserName === 'MobileSafari' && OSFamily === 'iOS') {
            emit('student.left', { id });
        }
    }, [BrowserName, emit, id, OSFamily]);

    const handleFocus = useCallback(() => {
        if (BrowserName === 'MobileSafari' && OSFamily === 'iOS') {
            emit('student.join', { id });
        }
    }, [BrowserName, emit, id, OSFamily]);

    useEffect(() => {
        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return function () {
            window.addEventListener('blur', handleBlur);
            window.addEventListener('focus', handleFocus);
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [handleBeforeUnload, handleBlur, handleFocus, handlePageHide, handlePageShow]);

    const handleAnswerClick = useCallback(
        (answerIndex: number) => {
            emit('student.answer', { answerIndex, questionIndex: currentQuestionIndex, studentId: id });
            if (currentQuestion) {
                setCurrentQuestionAnswer({
                    answerIndex,
                    answerValue: currentQuestion.answers[answerIndex].value,
                    title: currentQuestion.title || '',
                });
            }
            setCurrentQuestion(null);
        },
        [currentQuestionIndex, id, emit, setCurrentQuestion, currentQuestion]
    );

    const roomStudent = `quiz:${pin}:${id}`;

    useSocketRoom(roomStudent, {
        connect: () => emit('student.join', { id }),
        'initial.name': (teamName: string) => setName(teamName),
        question: ({ question, questionIndex }: { question: Question; questionIndex: number }) => {
            setCurrentQuestion(question);
            setCurrentQuestionIndex(questionIndex);
            setAnswerStatus('pending');
            setCompleted(false);
        },
        'show.answerStatus': (status: AnswerStatus) => showAnswerStatus(status),
        'show.results': () => setCompleted(true),
    });

    if (name) {
        if (isCompleted) {
            return <QuizStudentFinisher countCorrectAnswers={countCorrectAnswers} name={name} />;
        }

        if (!currentQuestion) {
            if (!answerStatus) {
                return (
                    <QuizStudentWrapper>
                        <div className="quiz-student__section">
                            <H1
                                id="quiz.waitingMessage"
                                mix="quiz-student__section-title"
                                typography={UI_KIT_HEADER_TYPOGRAPHY.headlineMMedium}
                            />
                        </div>
                    </QuizStudentWrapper>
                );
            }

            if (isVisibleCorrectAnswer) {
                if (answerStatus === 'correct' || answerStatus === 'incorrect') {
                    return (
                        <QuizStudentAnswerStatus
                            answerStatus={answerStatus}
                            name={name}
                            questionIndex={currentQuestionIndex}
                            questionsCount={questionsCount}
                        />
                    );
                }

                return (
                    <QuizStudentWrapper>
                        <div className="quiz-student__section">
                            <H2
                                id={'quiz.answer.status.none.title'}
                                mix="quiz-student__section-title"
                                typography={UI_KIT_HEADER_TYPOGRAPHY.headlineMMedium}
                            />
                        </div>
                    </QuizStudentWrapper>
                );
            } else if (currentQuestionAnswer) {
                return (
                    <QuizStudentWrapper>
                        <div className="quiz-student__section">
                            <H1 mix="quiz-student__section-title" typography={UI_KIT_HEADER_TYPOGRAPHY.headlineXLBold}>
                                {currentQuestionAnswer?.title}
                            </H1>
                            <H3
                                id="quiz.youChose.title"
                                mix="quiz-student__section-description"
                                typography={UI_KIT_HEADER_TYPOGRAPHY.headlineXXSMedium}
                            />
                            <div className="quiz-student__current-answer-wrapper">
                                <div
                                    className={b('quiz-student__current-answer-background', {
                                        color: CARD_COLORS[currentQuestionAnswer.answerIndex],
                                    })}
                                    key={currentQuestionAnswer?.answerValue}
                                />
                                <div
                                    className={b('quiz-student__current-answer', {
                                        color: CARD_COLORS[currentQuestionAnswer.answerIndex],
                                    })}
                                    key={currentQuestionAnswer?.answerValue}
                                >
                                    <H1 typography={UI_KIT_HEADER_TYPOGRAPHY.headlineXLBold}>
                                        {currentQuestionAnswer?.answerValue}
                                    </H1>
                                </div>
                            </div>
                            <H3
                                id="quiz.waitingCorrectMessage.title"
                                mix="quiz-student__section-description"
                                typography={UI_KIT_HEADER_TYPOGRAPHY.headlineXXSMedium}
                            />
                        </div>
                    </QuizStudentWrapper>
                );
            }
        }

        return (
            <QuizStudentQuestion
                name={name}
                onAnswerClick={handleAnswerClick}
                question={currentQuestion}
                questionIndex={currentQuestionIndex}
                questionsCount={questionsCount}
            />
        );
    }

    return (
        <QuizStudentWrapper>
            <div className="quiz-student__section">
                <H4
                    id="quiz.educationYandexQuiz"
                    mix="quiz-student__education-yandex-quiz-title"
                    typography={UI_KIT_HEADER_TYPOGRAPHY.headlineXXSMedium}
                />
                <H1
                    id="quiz.waitingTeacher.header"
                    mix="quiz-student__waiting-teacher-title"
                    typography={UI_KIT_HEADER_TYPOGRAPHY.headlineXXLBold}
                />
                <Spin mix="quiz-student__spin" size={SPIN_SIZES.XXL} />
            </div>
        </QuizStudentWrapper>
    );
};
